import type { DictionaryEntry, EntrySearchRow, EntryStatus, EntryType } from '../types'
import { STATUS_LABELS, TYPE_LABELS } from '../types'

// ── Status badge ─────────────────────────────────────────────────────────────

export function StatusBadge({ status }: { status: EntryStatus }) {
  const cls =
    status === 'candidate_high_priority' ? 'badge-high-priority' :
    status === 'needs_review'            ? 'badge-needs-review'  :
    status === 'candidate_low_priority'  ? 'badge-low-priority'  :
    'badge-unverified'

  return <span className={cls}>{STATUS_LABELS[status]}</span>
}

// ── Entry type badge ──────────────────────────────────────────────────────────

export function TypeBadge({ type }: { type: EntryType }) {
  const cls = type === 'grammar_marker' ? 'badge-grammar' : 'badge-lexical'
  return <span className={cls}>{TYPE_LABELS[type]}</span>
}

// ── Compact evidence chips (used in search cards) ─────────────────────────────

export function EvidenceChips({
  entry,
}: {
  entry: Pick<
    DictionaryEntry | EntrySearchRow,
    'has_eflomal_support' | 'has_tamang_neural_support' | 'alignment_consistency_score'
  >
}) {
  const consistent = entry.alignment_consistency_score !== null &&
                     entry.alignment_consistency_score > 0

  return (
    <div className="flex flex-wrap gap-1.5 text-xs">
      <Chip label="eflomal"       active={entry.has_eflomal_support} />
      <Chip label="neural"        active={entry.has_tamang_neural_support} />
      {consistent && (
        <span className="chip-yes">✓ consistent</span>
      )}
    </div>
  )
}

function Chip({ label, active }: { label: string; active: boolean }) {
  return (
    <span className={active ? 'chip-yes' : 'chip-no'}>
      {active ? '✓' : '–'} {label}
    </span>
  )
}

// ── Full evidence section (used in entry detail page) ─────────────────────────

export function EvidenceSection({ entry }: { entry: DictionaryEntry }) {
  const sources = entry.alignment_sources.split('|').filter(Boolean)
  const consistent = entry.alignment_consistency_score !== null &&
                     entry.alignment_consistency_score > 0

  return (
    <div className="rounded-xl border border-stone-200 bg-stone-50 p-4 space-y-3">
      <h3 className="text-sm font-semibold text-stone-600 uppercase tracking-wider">
        Alignment Evidence
      </h3>

      <div className="flex flex-wrap gap-2">
        {sources.map(s => (
          <span key={s} className="chip-yes">{s}</span>
        ))}
        {consistent && (
          <span className="chip-yes">✓ eflomal + neural agree</span>
        )}
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 text-sm text-stone-600">
        <Stat label="Frequency" value={String(entry.frequency)} />
        <Stat label="Status"    value={STATUS_LABELS[entry.status]} />
        <Stat label="Type"      value={TYPE_LABELS[entry.entry_type]} />
        <Stat label="True roundtrip" value={entry.has_true_roundtrip_score ? 'yes' : 'no — no MT checkpoint'} />
      </div>

      <p className="text-xs text-stone-400">
        All alignment scores are machine-generated. Alignment consistency means both
        eflomal and neural methods independently proposed the same word pair.
        This does NOT constitute human verification.
      </p>
    </div>
  )
}

function Stat({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <dt className="text-xs text-stone-400 uppercase tracking-wide">{label}</dt>
      <dd className="font-medium">{value}</dd>
    </div>
  )
}
